/**
 * @engine/input — Vector3Control
 *
 * 3D analog control for accelerometer, gyroscope, etc.
 */

import type { ControlType, Vec3Like } from '../types';
import { InputControl } from './InputControl';
import { AxisControl } from './AxisControl';

/** Default zero vector */
const ZERO: Vec3Like = { x: 0, y: 0, z: 0 };

/**
 * Vector3 state interface
 */
export interface Vector3State {
  readonly value: Vec3Like;
  readonly previousValue: Vec3Like;
  readonly delta: Vec3Like;
  readonly magnitude: number;
}

/**
 * Vector3 control — 3D analog value (accelerometer, gyroscope)
 */
export class Vector3Control extends InputControl<Vec3Like> implements Vector3State {
  readonly type: ControlType = 'vector3';

  /** Child axis controls for individual X/Y/Z access */
  readonly x: AxisControl;
  readonly y: AxisControl;
  readonly z: AxisControl;

  constructor(name: string) {
    super(name, ZERO);
    this.x = new AxisControl(`${name}/x`, true);
    this.y = new AxisControl(`${name}/y`, true);
    this.z = new AxisControl(`${name}/z`, true);
  }

  // ============================================================================
  // Vector3State interface
  // ============================================================================

  get delta(): Vec3Like {
    return {
      x: this._value.x - this._previousValue.x,
      y: this._value.y - this._previousValue.y,
      z: this._value.z - this._previousValue.z,
    };
  }

  get magnitude(): number {
    const { x, y, z } = this._value;
    return Math.sqrt(x * x + y * y + z * z);
  }

  // ============================================================================
  // Override for deep value comparison
  // ============================================================================

  protected override valuesEqual(a: Vec3Like, b: Vec3Like): boolean {
    return a.x === b.x && a.y === b.y && a.z === b.z;
  }

  // ============================================================================
  // Update
  // ============================================================================

  override update(newValue: Vec3Like, time: number): void {
    super.update(newValue, time);
    this.x.update(newValue.x, time);
    this.y.update(newValue.y, time);
    this.z.update(newValue.z, time);
  }

  override reset(): void {
    super.reset();
    this.x.reset();
    this.y.reset();
    this.z.reset();
  }

  override commitFrame(): void {
    super.commitFrame();
    this.x.commitFrame();
    this.y.commitFrame();
    this.z.commitFrame();
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  /**
   * Get normalized direction
   */
  get normalized(): Vec3Like {
    const mag = this.magnitude;
    if (mag < 0.0001) return ZERO;
    return { x: this._value.x / mag, y: this._value.y / mag, z: this._value.z / mag };
  }
}
